import { Controller } from "@hotwired/stimulus"

// Reusable controller for Bootstrap modals that load content via Turbo Frames
//
// Usage:
//   <div class="modal" data-controller="turbo-frame-modal"
//        data-turbo-frame-modal-frame-id-value="my_frame"
//        data-turbo-frame-modal-src-param-value="account_id">
//     <div class="modal-body">
//       <%= turbo_frame_tag "my_frame" do %>
//         Loading...
//       <% end %>
//     </div>
//   </div>
//
//   <button data-bs-toggle="modal" data-bs-target="#myModal" data-account-id="123">
//     Open Modal
//   </button>
//
export default class extends Controller {
  static values = {
    frameId: String,      // ID of the turbo frame to load
    srcPath: String,      // Base path for the frame src (e.g., "/admin/tax-organizers/new")
    srcParam: String      // Name of the data attribute to read from the button (e.g., "account-id")
  }

  connect() {
    // Listen for Bootstrap modal events
    this.element.addEventListener('show.bs.modal', this.handleShow.bind(this))
    this.element.addEventListener('hidden.bs.modal', this.handleHidden.bind(this))

    // Listen for successful form submissions to auto-close modal
    document.addEventListener('turbo:submit-end', this.handleSubmitEnd.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('show.bs.modal', this.handleShow.bind(this))
    this.element.removeEventListener('hidden.bs.modal', this.handleHidden.bind(this))
    document.removeEventListener('turbo:submit-end', this.handleSubmitEnd.bind(this))
  }

  handleShow(event) {
    // Get the button that triggered the modal
    const button = event.relatedTarget
    if (!button) return

    // Get the turbo frame
    const frame = document.getElementById(this.frameIdValue)
    if (!frame) {
      console.error(`Turbo frame with id "${this.frameIdValue}" not found`)
      return
    }

    // Build the src URL
    let src

    // Option 1: Use the button's href if it's a link (but not if it's just "#")
    if (button.href && !button.href.endsWith('#')) {
      src = button.href
    }
    // Option 2: Build from srcPath and srcParam values
    else if (this.srcPathValue) {
      src = this.srcPathValue

      // Add query parameter if srcParam is specified
      if (this.srcParamValue) {
        const paramValue = button.getAttribute(`data-${this.srcParamValue}`)
        if (paramValue) {
          const separator = src.includes('?') ? '&' : '?'
          // Convert kebab-case to snake_case for Rails params
          const paramName = this.srcParamValue.replace(/-/g, '_')
          src = `${src}${separator}${paramName}=${paramValue}`
        }
      }
    }

    if (!src) {
      console.error('No src URL found for turbo frame')
      return
    }

    // Set the frame src to load content
    frame.src = src
  }

  handleHidden() {
    // Reset the turbo frame when modal closes
    const frame = document.getElementById(this.frameIdValue)
    if (frame) {
      frame.removeAttribute('src')
      // Restore loading state
      frame.innerHTML = '<div class="text-center text-muted py-4"><i class="fas fa-circle-notch fa-spin fa-2x mb-2"></i><p>Loading...</p></div>'
    }
  }

  handleSubmitEnd(event) {
    // Check if the form submission was successful and within this modal
    if (event.detail.success && event.target.closest(`#${this.element.id}`)) {
      // Allow forms to opt-out of auto-close behavior
      if (event.target.dataset.skipModalAutoClose === 'true') {
        return
      }

      const modalInstance = bootstrap.Modal.getInstance(this.element)
      if (modalInstance) {
        modalInstance.hide()
      }
    }
  }
}
